"use client";

import { useCallback, useEffect, useState } from "react";
import {
  BIBLIOGRAPHY_CHANGE_EVENT,
  BIBLIOGRAPHY_STORAGE_KEY,
  type Bibliography,
  type CitationSortMode,
  DEFAULT_SORT_MODE,
  getActiveBibliography,
  listBibliographies,
  loadBibliography,
  type SavedCitation,
} from "@/lib/bibliography";

const SORT_STORAGE_KEY = "citationeasy-bibliography-sort";

const SORT_MODES: CitationSortMode[] = [
  "author-asc",
  "date-desc",
  "year-asc",
  "manual",
];

export function isSortMode(value: string): value is CitationSortMode {
  return SORT_MODES.includes(value as CitationSortMode);
}

function loadSortMode(): CitationSortMode {
  if (typeof window === "undefined") {
    return DEFAULT_SORT_MODE;
  }
  const stored = localStorage.getItem(SORT_STORAGE_KEY);
  return stored && isSortMode(stored) ? stored : DEFAULT_SORT_MODE;
}

export interface StudioStore {
  citations: SavedCitation[];
  activeBib: Bibliography | null;
  bibliographies: Bibliography[];
  /** `true` once localStorage has been read on the client. */
  loaded: boolean;
  sortMode: CitationSortMode;
  setSortMode: (mode: CitationSortMode) => void;
  reload: () => void;
}

/**
 * The studio's single view of localStorage: the active project's citations, the
 * project list, and the persisted sort order. Re-reads on the bibliography module's
 * same-tab change event and on cross-tab `storage` events, so every write anywhere
 * on the site shows up here without plumbing.
 */
export function useStudioStore(): StudioStore {
  const [citations, setCitations] = useState<SavedCitation[]>([]);
  const [activeBib, setActiveBib] = useState<Bibliography | null>(null);
  const [bibliographies, setBibliographies] = useState<Bibliography[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [sortMode, setSortModeState] =
    useState<CitationSortMode>(DEFAULT_SORT_MODE);

  const reload = useCallback(() => {
    setCitations(loadBibliography());
    setActiveBib(getActiveBibliography());
    setBibliographies(listBibliographies());
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload();
    setSortModeState(loadSortMode());
  }, [reload]);

  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key === BIBLIOGRAPHY_STORAGE_KEY) {
        reload();
      }
    }
    window.addEventListener(BIBLIOGRAPHY_CHANGE_EVENT, reload);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(BIBLIOGRAPHY_CHANGE_EVENT, reload);
      window.removeEventListener("storage", handleStorage);
    };
  }, [reload]);

  const setSortMode = useCallback((mode: CitationSortMode) => {
    setSortModeState(mode);
    localStorage.setItem(SORT_STORAGE_KEY, mode);
  }, []);

  return {
    citations,
    activeBib,
    bibliographies,
    loaded,
    sortMode,
    setSortMode,
    reload,
  };
}
